import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowLeft, Briefcase, DollarSign, MapPin, Clock, TrendingUp, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const STATUS_PILL = {
  assigned: 'bg-blue-100 text-blue-700',
  in_transit: 'bg-green-100 text-green-700',
  delivered: 'bg-slate-100 text-slate-600',
};

const BrokerPerformance = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(90);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/analytics/brokers?days=${days}`, { withCredentials: true });
        if (mounted) setData(res.data);
      } catch (e) {
        if (mounted) setError(e.response?.data?.detail || 'Failed to load');
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [days]);

  const chartData = useMemo(() => (data?.brokers || []).map(b => ({
    name: b.broker,
    revenue: b.revenue,
    rpm: b.avg_revenue_per_mile || 0,
  })), [data]);

  if (loading) return <DashboardLayout><div className="p-8 text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading broker performance...</div></DashboardLayout>;
  if (error) return <DashboardLayout><div className="p-8 text-red-700" data-testid="brokers-error">{error}</div></DashboardLayout>;
  if (!data) return null;

  const { fleet, brokers } = data;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="broker-performance-page">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4" data-testid="back-to-dashboard" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Broker Performance</h1>
            <p className="text-base text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Which brokers actually move loads and pay well.</p>
          </div>
          <select data-testid="window-select" value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last 12 months</option>
          </select>
        </div>

        {/* Fleet KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KpiCard testid="kpi-total-brokers" icon={Briefcase} label="Brokers" value={brokers.length} />
          <KpiCard testid="kpi-total-loads" icon={Clock} label="Total Loads" value={fleet.total_loads} onClick={() => navigate('/loads')} />
          <KpiCard testid="kpi-delivered-loads" icon={MapPin} label="Delivered" value={fleet.delivered_loads} color="#34C759" onClick={() => navigate('/loads?status=delivered')} />
          <KpiCard testid="kpi-revenue" icon={DollarSign} label="Revenue" value={fmtMoney(fleet.revenue)} color="#002FA7" />
        </div>

        {brokers.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center" data-testid="no-brokers">
            <Briefcase className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No broker data yet. Add a broker name on any load and they'll appear here.</p>
          </div>
        ) : (
          <>
            <div className="bg-white border border-slate-200 rounded-md p-6 mb-8" data-testid="broker-chart">
              <h2 className="text-lg font-semibold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>Revenue by Broker</h2>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                  <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 24, left: 24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={(v) => fmtMoney(v)} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#64748B' }} width={140} />
                    <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12 }} />
                    <Bar dataKey="revenue" name="Revenue" fill="#002FA7" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="space-y-3" data-testid="broker-list">
              {brokers.map(b => <BrokerCard key={b.broker} b={b} navigate={navigate} />)}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

const BrokerCard = ({ b, navigate }) => (
  <div data-testid={`broker-card-${b.broker.replace(/\s+/g, '-').toLowerCase()}`} className="bg-white border border-slate-200 rounded-md p-5">
    <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 bg-[#002FA7]/10 rounded-md flex items-center justify-center flex-shrink-0">
          <Briefcase className="w-5 h-5 text-[#002FA7]" />
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-bold tracking-tight text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>{b.broker}</h3>
          <p className="text-xs text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{b.total_loads} loads · {b.delivered_loads} delivered{b.in_transit_loads ? ` · ${b.in_transit_loads} in transit` : ''}</p>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <Metric label="Revenue" value={fmtMoney(b.revenue)} highlight />
      <Metric label="Avg Rate" value={b.avg_rate != null ? fmtMoney(b.avg_rate) : '—'} />
      <Metric label="Avg $/Mile" value={b.avg_revenue_per_mile != null ? `$${b.avg_revenue_per_mile.toFixed(3)}` : '—'} />
      <Metric label="Miles" value={Math.round(b.miles).toLocaleString()} />
      <Metric label="Avg Days/Load" value={b.avg_days_to_deliver != null ? `${b.avg_days_to_deliver}d` : '—'} />
    </div>

    {b.recent?.length > 0 && (
      <div className="mt-4 pt-4 border-t border-slate-100">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Recent loads</p>
        <div className="space-y-1">
          {b.recent.map(l => (
            <button
              key={l.id}
              data-testid={`broker-recent-${l.id}`}
              onClick={() => navigate('/loads')}
              className="w-full text-left flex items-center gap-3 text-xs px-2 py-1.5 rounded hover:bg-slate-50"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <span className="font-mono text-slate-500 w-12 flex-shrink-0">{l.load_ref || `#${l.id.slice(-6)}`}</span>
              <span className="text-slate-700 flex-1 truncate">{l.pickup_location} → {l.delivery_location}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_PILL[l.status] || 'bg-slate-100 text-slate-600'}`}>{l.status?.replace('_', ' ')}</span>
              {l.load_rate != null && <span className="text-slate-700 font-semibold">{fmtMoney(l.load_rate)}</span>}
              <ArrowRight className="w-3 h-3 text-slate-400" />
            </button>
          ))}
        </div>
      </div>
    )}
  </div>
);

const Metric = ({ label, value, highlight }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`mt-0.5 font-bold ${highlight ? 'text-[#002FA7] text-xl' : 'text-slate-900 text-base'}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</p>
  </div>
);

const KpiCard = ({ testid, icon: Icon, label, value, color = '#002FA7', onClick }) => {
  const Component = onClick ? 'button' : 'div';
  return (
    <Component
      data-testid={testid}
      onClick={onClick}
      className={`bg-white border border-slate-200 rounded-md p-5 w-full text-left ${onClick ? 'cursor-pointer hover:border-[#002FA7] hover:shadow-sm transition-all' : ''}`}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-7 h-7" style={{ color }} strokeWidth={2} />
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</p>
          <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</p>
        </div>
      </div>
    </Component>
  );
};

export default BrokerPerformance;
